import { useState } from 'react'
import type { Draft, DraftPayload, Member, ReportStatus } from '@cs/shared'
import { ApiError, api } from '../api.js'
import { describeError } from '../App.js'

const KIND_LABEL: Record<Draft['kind'], string> = {
  session_summary: 'セッション要約',
  doc_change: '仕様書の変更',
  message: '保留されたメッセージ',
}

/**
 * The approval queue is where the 承認制 policy actually happens: nothing here has
 * left this member's company yet, and the approver can rewrite it before it does.
 */
export function Approvals({
  room,
  drafts,
  members,
  meId,
  onChanged,
}: {
  room: string
  drafts: Draft[]
  members: Member[]
  meId: string
  onChanged: () => void
}) {
  // Who this actually goes to, named. "相手に見えます" is abstract enough to click
  // past; "八十島 (営業会社) に見えます" is the sentence that makes someone read.
  const audience = members.filter((m) => m.user_id !== meId)
  const [error, setError] = useState<string | null>(null)

  if (drafts.length === 0) {
    return (
      <div className="card">
        <h2>共有待ち</h2>
        <p className="muted">
          共有待ちはありません。あなたの Claude が出した要約・仕様変更・保留されたメッセージはここに並び、
          あなたが共有するまで相手からは見えません。
        </p>
      </div>
    )
  }

  return (
    <>
      {error && <div className="error">{error}</div>}
      <div className="card">
        <h2>共有待ち {drafts.length} 件</h2>
        <p className="muted" style={{ margin: 0 }}>
          ここで決めるのは<strong>相手に出すか出さないか</strong>だけです。内容の良し悪しの判断では
          ありません。出さないものを「共有しない」にしても、手元の作業には何の影響もありません。
        </p>
      </div>
      {drafts.map((d) => (
        <DraftCard
          key={d.id}
          room={room}
          draft={d}
          audience={audience}
          onChanged={onChanged}
          onError={setError}
        />
      ))}
    </>
  )
}

function DraftCard({
  room,
  draft,
  audience,
  onChanged,
  onError,
}: {
  room: string
  draft: Draft
  audience: Member[]
  onChanged: () => void
  onError: (msg: string) => void
}) {
  const [payload, setPayload] = useState<DraftPayload>(draft.payload)
  const [busy, setBusy] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')

  const edited = JSON.stringify(payload) !== JSON.stringify(draft.payload)

  /**
   * A draft can disappear while its card is on screen: a rolling session summary is
   * replaced by the next one from the same session, and the old row is deleted. The
   * stream event now removes the stale card, but a tab that missed it would still
   * send an approve for a row that no longer exists — so say what happened and
   * resync rather than showing a bare 404.
   */
  const handle = (err: unknown) => {
    if (err instanceof ApiError && err.status === 404) {
      onError(
        'この下書きは、同じセッションの新しい要約に差し替えられたため、もうありません。' +
          '最新のものを表示しました。',
      )
      onChanged()
      return
    }
    onError(describeError(err))
  }

  const approve = async () => {
    setBusy(true)
    try {
      await api.approve(room, draft.id, edited ? payload : undefined)
      onChanged()
    } catch (err) {
      handle(err)
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    setBusy(true)
    try {
      await api.reject(room, draft.id, reason)
      onChanged()
    } catch (err) {
      handle(err)
    } finally {
      setBusy(false)
    }
  }

  /**
   * The button says who receives it.
   *
   * "承認して公開" reads as a verdict on the content — the reviewer asks themselves
   * whether the work was any good, when the only question is whether this text
   * should leave the company. Naming the recipient removes the ambiguity: nobody
   * misreads 「八十島さんに共有する」 as an opinion about the work.
   */
  const shareLabel =
    audience.length === 0
      ? '共有する'
      : audience.length <= 2
        ? `${audience.map((m) => m.display_name).join('・')}さんに共有する`
        : `${audience.length}人に共有する`

  const blocking = draft.findings.filter((f) => f.severity === 'block')
  const warning = draft.findings.filter((f) => f.severity === 'warn')

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="pill decision">{KIND_LABEL[draft.kind]}</span>
        {draft.from_agent && <span className="pill agent">AI が作成</span>}
        <span className="muted">{new Date(draft.created_at).toLocaleString('ja-JP')}</span>
      </div>

      {blocking.length > 0 && (
        <div className="error">
          <strong>秘密情報の可能性:</strong>{' '}
          {blocking.map((f) => `${f.label} (${f.excerpt})`).join(' / ')}
          <br />
          この内容が本当に社外に出てよいか確認してください。
        </div>
      )}
      {warning.length > 0 && (
        <div className="notice">
          確認: {warning.map((f) => `${f.label} (${f.excerpt})`).join(' / ')}
        </div>
      )}

      {payload.kind === 'session_summary' && (
        <>
          <label className="muted">タイトル</label>
          <input
            value={payload.title}
            onChange={(e) => setPayload({ ...payload, title: e.target.value })}
          />
          <div style={{ height: 8 }} />
          <label className="muted">状況</label>
          <select
            value={payload.status}
            onChange={(e) => setPayload({ ...payload, status: e.target.value as ReportStatus })}
            style={{ width: 'auto' }}
          >
            <option value="working">作業中</option>
            <option value="blocked">ブロック中</option>
            <option value="done">完了</option>
          </select>
          <div style={{ height: 8 }} />
          <label className="muted">本文</label>
          <textarea
            value={payload.body}
            style={{ minHeight: 140 }}
            onChange={(e) => setPayload({ ...payload, body: e.target.value })}
          />
        </>
      )}

      {payload.kind === 'doc_change' && (
        <>
          <div className="kv" style={{ marginBottom: 8 }}>
            <span className="muted">パス</span>
            <code>{payload.path}</code>
            <span className="muted">メモ</span>
            <span>{payload.note || '—'}</span>
          </div>
          <label className="muted">変更後の全文</label>
          <textarea
            value={payload.content}
            style={{ minHeight: 260, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
            onChange={(e) => setPayload({ ...payload, content: e.target.value })}
          />
        </>
      )}

      {payload.kind === 'message' && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            送信されずに保留されたメッセージです。
          </p>
          <textarea
            value={payload.body}
            style={{ minHeight: 120 }}
            onChange={(e) => setPayload({ ...payload, body: e.target.value })}
          />
        </>
      )}

      {rejecting ? (
        <div style={{ marginTop: 10 }}>
          <label className="muted">共有しない理由 (任意・自分の記録用)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setRejecting(false)}>やめる</button>
            <button className="danger" disabled={busy} onClick={reject}>
              共有しない
            </button>
          </div>
        </div>
      ) : confirming ? (
        /*
         * A deliberate second step. The content is right above and always has been,
         * but one click from "there is something in the queue" to "another company
         * can read it" is too short a path — this is the point at which people
         * approve without looking. Naming the recipients is the part that works.
         */
        <div className="notice" style={{ marginTop: 12 }}>
          <strong>共有すると、この内容は相手の会社から読めるようになります。</strong>
          <div style={{ marginTop: 6 }}>
            読めるようになる相手:{' '}
            {audience.length === 0 ? (
              <span className="muted">まだ他のメンバーがいません（自分だけの状態です）</span>
            ) : (
              audience.map((m) => `${m.display_name} (${m.org})`).join(' / ')
            )}
          </div>
          {(blocking.length > 0 || warning.length > 0) && (
            <div style={{ marginTop: 6 }}>
              未解決の指摘が {blocking.length + warning.length} 件あります。
            </div>
          )}
          <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirming(false)} disabled={busy}>
              戻って確認する
            </button>
            <button className="primary" disabled={busy} onClick={approve}>
              {shareLabel}
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          {edited && <span className="muted">編集済み — 編集後の内容が共有されます</span>}
          <button onClick={() => setRejecting(true)} disabled={busy}>
            共有しない
          </button>
          <button className="primary" onClick={() => setConfirming(true)} disabled={busy}>
            {shareLabel}
          </button>
        </div>
      )}
    </div>
  )
}
